import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { cn } from '../lib/utils';
import Header from './Header';
import Sidebar from './Sidebar';

const SIDEBAR_COLLAPSED_KEY = 'grow-sidebar-collapsed';

function AppLayout({ children, title }) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved ? JSON.parse(saved) : false;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Close mobile menu on route change or resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  return (
    <>
      <Helmet>
        <title>{title} | Grow</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block">
          <Sidebar isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} />
        </div>

        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <>
            <div
              className="fixed inset-0 z-30 bg-black/50 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Escape' && setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            />
            <div className="fixed left-0 top-0 z-40 lg:hidden">
              <Sidebar isCollapsed={false} onToggle={() => setIsMobileMenuOpen(false)} />
            </div>
          </>
        )}

        {/* Main Content */}
        <div
          className={cn(
            'transition-all duration-300 h-screen flex flex-col',
            isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64',
          )}
        >
          <Header title={title} onMenuClick={toggleMobileMenu} showMenuButton />
          <main className="p-6 flex-1 overflow-hidden flex flex-col">{children}</main>
        </div>
      </div>
    </>
  );
}

AppLayout.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
};

export default AppLayout;
